import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rpfi09PosGroup, Rpfi09TableResult } from '../../../../core/models/report/rpfi09.model';
import { MapperRpfi09Service } from '../../../../core/mappers/report/mapper-rpfi09.service';
import { ApiRpfi09Service } from '../../../../core/services/api/report/api-rpfi09.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rpfi09',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rpfi09.component.html',
  styleUrl: './rpfi09.component.css',
})
export class Rpfi09Component {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRpfi09Service);
  private readonly mapper = inject(MapperRpfi09Service);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPFI09.pdf';

  startDate = '';
  endDate = '';
  lastFetchedTime: Date | null = null;
  hasSearched = signal(false);

  private readonly maxRangeMonths = 2;
  result = signal<Rpfi09TableResult | null>(null);

  constructor() {
    const today = this.toInputDate(new Date());
    this.startDate = today;
    this.endDate = today;

    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => this.clearResult());
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get groups(): Rpfi09PosGroup[] {
    return this.result()?.groups ?? [];
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRpfi09ForTable({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPFI09 ไม่สำเร็จ');
      },
    });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.groups.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = [];
      for (const g of this.groups) {
        for (const r of g.rows) {
          exportRows.push({
            'ลำดับ': r.seq,
            'จุดรับชำระเงิน': g.posName,
            'วันเวลา': this.formatDateTime(r.paidDate),
            'แคชเชียร์': r.cashierName,
            'เลขที่ใบเสร็จ': r.receiptId,
            'สถานะ': r.statusLabel,
            'ค่าบริการ': r.fee,
            'ภาษีมูลค่าเพิ่ม': r.vat,
            'รวมทั้งสิ้น': r.total,
          });
        }
        exportRows.push({
          'ลำดับ': '',
          'จุดรับชำระเงิน': `รวม ${g.posName}`,
          'วันเวลา': '',
          'แคชเชียร์': '',
          'เลขที่ใบเสร็จ': '',
          'สถานะ': '',
          'ค่าบริการ': g.sumFee,
          'ภาษีมูลค่าเพิ่ม': g.sumVat,
          'รวมทั้งสิ้น': g.sumTotal,
        });
      }
      const t = this.totals!;
      exportRows.push({
        'ลำดับ': '',
        'จุดรับชำระเงิน': `ยอดรวมทั้งสิ้น (${t.rowCount} รายการ)`,
        'วันเวลา': '',
        'แคชเชียร์': '',
        'เลขที่ใบเสร็จ': '',
        'สถานะ': '',
        'ค่าบริการ': t.sumFee,
        'ภาษีมูลค่าเพิ่ม': t.sumVat,
        'รวมทั้งสิ้น': t.sumTotal,
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      await downloadReportExcel(exportRows, {
        reportTitle: 'RPFI09 — รายงานรายละเอียดภาษีขาย',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        sheetName: 'RPFI09',
        fileName: buildReportExcelFilename('RPFI09', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPFI09_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getRpfi09Pdf({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getRpfi09Pdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
